/**
 * TND-009 — le registre des échanges avec l'acheteur, lu EN ENTIER.
 *
 * ## 🔴 Ce qui manquait : la suite
 *
 * La route pagine (`limit` : 50 par défaut, 100 au plus ; `next_cursor`, `has_more`) et
 * `useTenderQuestions` n'en lisait que la PREMIÈRE page : au-delà des 50 échanges les plus
 * récents, la liste s'arrêtait sans un mot. La bannière « réponse qui modifie le DCE »
 * (`TenderQuestionsPanel`) et le compteur de la Synthèse (« N demandes de l'acheteur sans
 * réponse », `TenderSynthesisBody`) comptaient sur ce tableau tronqué.
 *
 * Un registre de PREUVE se lit en entier : on suit `next_cursor`, par pages de 100, tant
 * que `has_more` ; l'écran reçoit toujours UN tableau, dans l'ordre du serveur (le plus
 * récent en tête).
 *
 * ⚠️ Deux bornes pour ne jamais tourner sans fin : `PAGES_AU_PLUS`, et une page qui
 * n'apporte AUCUN échange nouveau. La route retombe EN SILENCE sur la première page quand
 * elle ne sait pas lire le curseur (`questions_routes._decode_cursor`) : comparer le curseur
 * au précédent ne voyait que le curseur immobile, gardait la page resservie (chaque échange
 * deux fois) et laissait tourner jusqu'à la borne un curseur illisible APRÈS un lisible.
 * Les `id` déjà lus ne sont jamais rendus deux fois.
 * ⚠️ Le curseur `"{iso}|{id}"` porte `+` et `:` : `URLSearchParams` l'ENCODE. Écrit tel quel
 * dans l'URL, `+00:00` arriverait en `' 00:00'`, illisible — donc la première page, en boucle.
 *
 * `lire` est le `getJson` du module des crochets, passé en argument pour ne pas importer
 * `useTenders.ts` d'ici (import circulaire), comme `useTenders.documentsPages`.
 */

import {
    TENDERS_ENDPOINT,
    type TenderQuestion,
    type TenderQuestionsPayload,
} from './useTenders.types'

/** Le plafond de la route (`limit`, `le=100`) : le moins d'allers-retours possible. */
export const ECHANGES_PAR_PAGE = 100
/** 5 000 échanges : aucune consultation n'en porte autant ; la borne ne sert qu'à s'arrêter. */
export const PAGES_AU_PLUS = 50

/** L'URL d'une page. `URLSearchParams` ENCODE le curseur. */
export function urlDesEchanges(tenderId: string, entityId: string, curseur: string | null): string {
    const params = new URLSearchParams({ entity_id: entityId, limit: String(ECHANGES_PAR_PAGE) })
    if (curseur) params.set('cursor', curseur)
    return `${TENDERS_ENDPOINT}/tenders/${tenderId}/questions?${params}`
}

/** Toutes les pages du registre, mises bout à bout. */
export async function lireTousLesEchanges(
    tenderId: string,
    entityId: string,
    lire: <T>(url: string) => Promise<T>,
): Promise<TenderQuestion[]> {
    const echanges: TenderQuestion[] = []
    const dejaLus = new Set<string>()
    let curseur: string | null = null
    for (let page = 0; page < PAGES_AU_PLUS; page += 1) {
        const reponse: TenderQuestionsPayload = await lire<TenderQuestionsPayload>(
            urlDesEchanges(tenderId, entityId, curseur),
        )
        const nouveaux = (reponse.items ?? []).filter((echange) => !dejaLus.has(echange.id))
        for (const echange of nouveaux) dejaLus.add(echange.id)
        echanges.push(...nouveaux)
        if (!reponse.has_more || !reponse.next_cursor || nouveaux.length === 0) break
        curseur = reponse.next_cursor
    }
    return echanges
}
